/**
 * ENG-8359 (ADR-0045 / ENG-7543): the per-connection naming rule for remote-MCP
 * integrations — the single place that decides what a NAMED connection is called
 * on disk.
 *
 * Named connections (`connection_key`) let one integration definition be
 * installed more than once on an agent (two Brand Ninja accounts, two mailboxes).
 * Every such connection needs its own `.mcp.json` server entry AND its own
 * credential env var; keying either by `definition_id` alone makes the second
 * connection silently overwrite the first.
 *
 * This module is deliberately dependency-free so both the provisioning writer
 * (`@augmented/core` claudecode adapter) and the host-side readers (`apps/cli`
 * connectivity probe, manager orphan reaper) can import the SAME rule. A rule
 * that lives in only one of them is the two-allowlist failure mode: the writer
 * and the reader each filter on their own copy, and a divergence shows up as a
 * server that is written then immediately reaped, or probed at a key that was
 * never written — with no error in either case.
 *
 * ## Why the default and named forms look different
 *
 * The DEFAULT connection's server key is the RAW `definition_id`
 * (`brand-ninja`), because that is what the writer has always emitted and the
 * entire existing fleet is provisioned under it. Changing it would re-key every
 * agent's `.mcp.json` on the next provision — a fleet-wide churn (and a session
 * restart) to fix a defect no default connection has. So the default is
 * byte-identical, forever.
 *
 * A NAMED connection's key is `<sanitized-base>-<connection_key>`, which is
 * NOT the raw id plus a suffix. Two independent reasons force this:
 *
 *  1. **Injectivity.** A raw `definition_id` may contain hyphens, so
 *     `${rawId}-${key}` is ambiguous: definition `brand-ninja` + connection
 *     `secondary` and a definition literally named `brand-ninja-secondary`
 *     produce the same string. A SANITIZED base ranges over `[a-z0-9_]` and
 *     contains no hyphen, while a `connection_key` is DB-constrained to
 *     `^[a-z0-9][a-z0-9-]*$` (migration `20260709000008`) and contains no
 *     underscore — so `b1-k1 === b2-k2` splits uniquely on the FIRST hyphen and
 *     implies `b1 === b2 && k1 === k2`. This is the same argument the managed
 *     (Composio) lane already relies on in `managedConnectionKeySuffix`.
 *
 *  2. **Probe parity.** The host's connectivity probe already derives
 *     `sanitize(definition_id)-connection_key` for a named connection
 *     (`deriveMcpServerKey`), and since ENG-8345 it requires an EXACT declared
 *     match for a suffixed key — it deliberately will NOT fall back to the bare
 *     key, because probing the default connection's server and reporting that as
 *     the named connection's health is a false GREEN. Emitting the sanitized
 *     form here is what makes writer and probe agree.
 *
 * Residual ambiguity, stated rather than hidden: a default key is a raw
 * `definition_id` and `integration_definitions.code_name` carries no character
 * CHECK, so a (pathological) definition literally named `brand_ninja-secondary`
 * would collide with brand-ninja's `secondary` connection. That is a property of
 * raw default keys which predates this module, not something the named form
 * introduces; the catalog is the control point.
 */

/** The `connection_key` value meaning "the one, unnamed connection". */
export const DEFAULT_REMOTE_MCP_CONNECTION_KEY = 'default';

/**
 * True when this connection is the default one — absent, empty, or the literal
 * `'default'`. Both the DB default and rows written before ENG-7543 land here,
 * which is what keeps the existing fleet byte-identical.
 */
export function isDefaultRemoteMcpConnection(connectionKey?: string | null): boolean {
  return !connectionKey || connectionKey === DEFAULT_REMOTE_MCP_CONNECTION_KEY;
}

/**
 * Sanitize a `definition_id` into a server-key base: `[a-z0-9_]`, no hyphen.
 * Byte-identical to the host-side `sanitizeServerKey` / `deriveMcpServerKey`
 * base and to `impersonationManagedServerKey`'s — the hyphen-free range is what
 * the injectivity argument above rests on, so it must not drift.
 */
function sanitizeBase(definitionId: string): string {
  return definitionId.replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

/**
 * The `.mcp.json` server key for one connection of a remote-MCP integration.
 *
 * - default/absent → the RAW `definition_id` (unchanged for the whole fleet)
 * - named          → `<sanitized-base>-<connection_key>`
 *
 * See the module docblock for why the two forms differ.
 */
export function remoteMcpServerKey(definitionId: string, connectionKey?: string | null): string {
  if (isDefaultRemoteMcpConnection(connectionKey)) return definitionId;
  return `${sanitizeBase(definitionId)}-${connectionKey}`;
}

/**
 * The credential-env-var infix for one connection, inserted after the
 * definition-derived prefix (`BRAND_NINJA` → `BRAND_NINJA__SECONDARY`).
 *
 * Fixing only the `.mcp.json` key would be worse than the clobber it replaces:
 * two distinct server entries would still read the SAME
 * `<DEFINITION_ID>_ACCESS_TOKEN` out of `.env.integrations`, so both
 * connections would authenticate as whichever row was written last — two
 * servers that both look healthy while pointing at one account. The credential
 * name has to carry the connection too.
 *
 * Empty for the default connection, so every existing env var name (and the
 * `<PREFIX>_ACCESS_TOKEN` convention documented to agents in CHARTER/skill text)
 * is untouched.
 *
 * `__` is the separator rather than `_` because the prefix transform maps `-` to
 * `_`: with a single underscore, definition `brand-ninja` + connection
 * `secondary` and definition `brand-ninja-secondary` would both yield
 * `BRAND_NINJA_SECONDARY_ACCESS_TOKEN`. A `connection_key` contains no
 * underscore and a kebab `definition_id` yields no `__`, so the doubled
 * separator splits unambiguously. (This mirrors why the managed tool-name lane
 * uses `__` as its connection separator in `namespacedManagedToolName`.)
 */
export function remoteMcpConnectionEnvInfix(connectionKey?: string | null): string {
  if (isDefaultRemoteMcpConnection(connectionKey)) return '';
  return `__${connectionKey!.replace(/-/g, '_').toUpperCase()}`;
}

/**
 * The definition-derived env-var prefix (`brand-ninja` → `BRAND_NINJA`), with
 * the connection infix applied. Every generic `<PREFIX>_*` var an integration
 * publishes to `.env.integrations` is built from this.
 */
export function remoteMcpEnvPrefix(definitionId: string, connectionKey?: string | null): string {
  const idPart = definitionId.toUpperCase().replace(/[^A-Z0-9]/g, '_');
  return `${idPart}${remoteMcpConnectionEnvInfix(connectionKey)}`;
}

/**
 * Re-point an env-var NAME that a remote-MCP spec references at this
 * connection's copy of it.
 *
 * A spec may name a var beyond the auth credential — Anchor's `headers` carry
 * `${ANCHOR_BROWSER_SESSION_ID}`, whose value comes from the integration's
 * `config`. Those are published under the same `<PREFIX>_*` convention, so a
 * named connection publishes `ANCHOR_BROWSER__SECOND_SESSION_ID` and the spec's
 * literal reference has to follow, or the second connection's server reads the
 * FIRST connection's session id — two servers that both look healthy while
 * driving one account, which is exactly the failure this issue is about, just
 * moved from the key to the value.
 *
 * Only a var that actually belongs to this definition is rewritten (it must
 * carry the definition's own prefix). Anything else is returned untouched: a
 * spec must not be able to reach another integration's secret, and quietly
 * re-pointing an unrecognised name is how it would.
 */
export function remoteMcpConnectionScopedEnvVar(
  definitionId: string,
  varName: string,
  connectionKey?: string | null,
): string {
  if (isDefaultRemoteMcpConnection(connectionKey)) return varName;
  const idPart = definitionId.toUpperCase().replace(/[^A-Z0-9]/g, '_');
  if (!varName.startsWith(`${idPart}_`)) return varName;
  return `${idPart}${remoteMcpConnectionEnvInfix(connectionKey)}_${varName.slice(idPart.length + 1)}`;
}

/**
 * A human-readable label for one connection, used for proxy log lines so two
 * connections of one definition are distinguishable in `manager.log`. Default →
 * the bare `definition_id` (unchanged).
 */
export function remoteMcpConnectionLabel(definitionId: string, connectionKey?: string | null): string {
  if (isDefaultRemoteMcpConnection(connectionKey)) return definitionId;
  return `${definitionId} (${connectionKey})`;
}
